import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { PluginListenerHandle } from '@capacitor/core';
import Voip, { Order, Token } from '@plugins/voip/voip.plugin';
import { AvatarOrderDataResponse } from '@api/client';
import { PlatformService } from '@services/platform.service';
import { AuthService } from '@services/auth.service';
import { PushRegistrationService } from '@app/services/push-registration.service';
import { NavigationStore } from '@services/navigation.store';
import { AvatarOrderService } from '@services/avatar-order.service';
import { CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';
import { NOTIFICATION_TYPE } from '@constants/app.constants';
import { AppNavigationService } from '@state-routing';

@Injectable({
  providedIn: 'root'
})
export class VoipNotificationsService {
  private readonly router: Router = inject(Router);
  private readonly pushRegistration = inject(PushRegistrationService);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly navigationStore: NavigationStore = inject(NavigationStore);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly avatarOrderService: AvatarOrderService = inject(AvatarOrderService);
  private readonly appNavigation: AppNavigationService = inject(AppNavigationService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private tokenListener: PluginListenerHandle | null = null;
  private orderListener: PluginListenerHandle | null = null;
  private orderListenerReady: Promise<void> | null = null;
  private initialization: {actorId: number; version: number; promise: Promise<void>} | null = null;
  private tokenListenerGeneration = 0;
  private navigationGeneration = 0;
  private destroyed = false;

  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.destroyed = true;
      this.tokenListenerGeneration++;
      this.navigationGeneration++;
      void this.removeListener(this.tokenListener);
      void this.removeListener(this.orderListener);
      this.tokenListener = null;
      this.orderListener = null;
    });
  }

  public async initializeVoipPushNotifications(): Promise<void> {
    if (this.destroyed || !this.platformService.isDevice || !this.platformService.isIos) return;
    const actorId = this.authService.parsedToken?.userId;
    const version = this.authService.sessionVersion;
    if (!actorId || !this.authService.isAuthenticated) return;
    if (this.initialization?.actorId === actorId && this.initialization.version === version) {
      return this.initialization.promise;
    }
    const generation = ++this.tokenListenerGeneration;
    const isCurrent = (): boolean => !this.destroyed && generation === this.tokenListenerGeneration
      && actorId === this.authService.parsedToken?.userId && version === this.authService.sessionVersion;
    const previous = this.tokenListener;
    this.tokenListener = null;
    const promise: Promise<void> = (async (): Promise<void> => {
      try {
        await this.initializeListeners(previous, isCurrent);
      } catch {
        if (this.initialization?.promise === promise) this.initialization = null;
        console.warn('VoIP listener initialization failed');
      }
    })();
    this.initialization = {actorId, version, promise};
    return promise;
  }

  private async initializeListeners(previous: PluginListenerHandle | null, isCurrent: () => boolean): Promise<void> {
    await this.removeListener(previous);
    if (!isCurrent()) return;
    await this.ensureOrderListener();
    if (!isCurrent()) return;
    // Native replays its current token on listener attachment, including after re-login.
    const listener = await Voip.addListener('voipPushRegistry', async (token: Token): Promise<void> => {
      if (!isCurrent()) return;
      try {
        await this.pushRegistration.register(token.token, 2, isCurrent);
      } catch {
        console.warn('VoIP push registration failed');
      }
    });
    if (!isCurrent()) {
      await this.removeListener(listener);
      return;
    }
    this.tokenListener = listener;
    try {
      await Voip.register();
    } catch (error) {
      if (this.tokenListener === listener) this.tokenListener = null;
      await this.removeListener(listener);
      throw error;
    }
  }

  private async ensureOrderListener(): Promise<void> {
    if (this.orderListenerReady) return this.orderListenerReady;
    // Do not remove/re-add this listener on login: native currently treats its presence as handoff.
    const installing = (async (): Promise<void> => {
      const listener = await Voip.addListener('handleOrder', (order: Order): Promise<void> => this.handleNativeOrder(order));
      if (this.destroyed) await this.removeListener(listener);
      else this.orderListener = listener;
    })();
    this.orderListenerReady = installing;
    try {
      await installing;
    } catch (error) {
      if (this.orderListenerReady === installing) this.orderListenerReady = null;
      throw error;
    }
  }

  private async handleNativeOrder(order: Order): Promise<void> {
    const orderId = order?.id;
    const actorId = this.authService.parsedToken?.userId;
    const version = this.authService.sessionVersion;
    if (this.destroyed || !actorId || !this.authService.isAuthenticated
      || !Number.isSafeInteger(orderId) || orderId <= 0) return;
    const generation = ++this.navigationGeneration;
    const isCurrent = (): boolean => !this.destroyed && generation === this.navigationGeneration
      && actorId === this.authService.parsedToken?.userId && version === this.authService.sessionVersion;
    const nativeType = typeof order.type === 'number' ? order.type : null;
    try {
      await this.goToCall(orderId, this.normalizeIncomingOrderType(nativeType), isCurrent);
    } catch {
      console.warn('VoIP call navigation failed');
    }
  }

  private async removeListener(listener: PluginListenerHandle | null): Promise<void> {
    if (!listener) return;
    try {
      await listener.remove();
    } catch {
      console.warn('VoIP listener cleanup failed');
    }
  }

  private async goToCall(orderId: number, orderType: number | null, isCurrent: () => boolean): Promise<void> {
    const navigationCommands: (string | number)[] = await this.resolveIncomingOrderNavigationCommands(orderId, orderType);
    if (!isCurrent()) return;

    await this.ngZone.run(async (): Promise<void> => {
      if (!isCurrent()) return;
      if (this.shouldSaveCallBackUrl(this.router.url)) {
        this.navigationStore.setBackUrl(this.router.url);
      }
      await this.appNavigation.navigateForward(navigationCommands, {replaceUrl: true}, isCurrent);
    });
  }

  private async resolveIncomingOrderNavigationCommands(orderId: number, orderType: number | null): Promise<(string | number)[]> {
    if (this.isAudioCallType(orderType)) {
      return ['/incoming-call', orderId];
    }
    if (typeof orderType === 'number') {
      return ['/avatar/order-details', orderId];
    }

    try {
      const response: AvatarOrderDataResponse = await this.avatarOrderService.getOrder(orderId, false);
      const resolvedOrderType: number | undefined = response?.data?.type;
      if (this.isAudioCallType(resolvedOrderType ?? null)) {
        return ['/incoming-call', orderId];
      }
    } catch {
      console.warn('VoIP order type lookup failed');
    }

    return ['/avatar/order-details', orderId];
  }

  private isAudioCallType(orderType: number | null): boolean {
    return orderType === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
  }

  private normalizeIncomingOrderType(rawType: number | null): number | null {
    if (rawType === null) return null;
    if (rawType < 0) return null;
    if (rawType === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL) return rawType;
    if (rawType === NOTIFICATION_TYPE.CALL) return CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
    return rawType;
  }

  private shouldSaveCallBackUrl(url: string): boolean {
    const normalizedUrl: string = url.toLowerCase();
    return !normalizedUrl.includes('/incoming-call')
      && !normalizedUrl.includes('/audio-call')
      && !normalizedUrl.includes('/video-call');
  }
}
